import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ClickAwayListener, Typography } from '@mui/material';

import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import SubHeading from 'src/App/Dashboards/Market/Components/Text/SubHeading';
import Body from 'src/App/Dashboards/Market/Components/Text/Body';
import ReferenceBarChart from 'src/App/Dashboards/Market/Components/Charts/ReferenceBarChart';
import { convertSnakeCaseToTitleCase } from 'src/App/Dashboards/Market/Components/Text/utils';
import ShowMoreButton from 'src/App/Dashboards/Market/Components/Buttons/ShowMoreButton';
import { calculateDynamicYAxisRange, abbreviateNumber, convertCurrency } from 'src/utils/functions';
import { amenitiesObject, BEST_PROPERTY_TYPE_FORMATTED_GRAPH_DATA, exchangeRates } from 'src/utils/const';
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Bar,
  Legend,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import {
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_300,
  COLOR_PRIMARY_1_400,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_2,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
} from 'src/utils/colors';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import StrPropertiesCTAButton from 'src/App/Dashboards/Market/Pages/EstimatorPage/UtilityComponents/RealtorForm/StrPropertiesCTAButton';

const COLORS = {
  budget: COLOR_PRIMARY_1_300,
  midscale: COLOR_PRIMARY_1_600,
  luxury: COLOR_PRIMARY_1,
};

const generatePropertyTypeSummaryMap = (
  propertyType: string,
  priceTier: { min: number; max: number },
  currency: string
) => {
  const currencySymbol = exchangeRates[currency].symbol;
  const priceMinText = abbreviateNumber(convertCurrency(currency, priceTier.min));
  const priceMaxText = abbreviateNumber(convertCurrency(currency, priceTier.max));

  const summaryMap = {
    'Small budget': `small budget stays (studios or 1 bedroom units, ${currencySymbol}${priceMaxText} or less per night)`,
    'Small midscale': `small midscale properties (studios or 1 bedroom units, ${currencySymbol}${priceMaxText} or less per night)`,
    'Small luxury': `small luxury homes (studios or 1 bedroom units, ${currencySymbol}${priceMinText} or more per night)`,
    'Medium budget': `medium budget stays (2-3 bedrooms, ${currencySymbol}${priceMaxText} or less per night)`,
    'Medium midscale': `medium midscale properties (2-3 bedrooms, ${currencySymbol}${priceMaxText} or less per night)`,
    'Medium luxury': `medium luxury homes (2-3 bedrooms, ${currencySymbol}${priceMinText} or more per night)`,
    'Large budget': `large budget stays (4+ bedrooms, ${currencySymbol}${priceMaxText} or less per night)`,
    'Large midscale': `large midscale properties (4+ bedrooms, ${currencySymbol}${priceMaxText} or less per night)`,
    'Large luxury': `large luxury homes (4+ bedrooms, ${currencySymbol}${priceMinText} or more per night)`,
  };

  return summaryMap[propertyType];
};

const generateSummaryText = (data: any[], currency: string, isUserAllowed: boolean) => {
  if (!isUserAllowed)
    return `The best-performing property types are *locked-sized locked homes (locked–locked bedrooms, ${exchangeRates[currency].symbol}locked - ${exchangeRates[currency].symbol}locked per night)* and *locked-sized locked stays (locked–locked bedrooms, ${exchangeRates[currency].symbol}locked - ${exchangeRates[currency].symbol}locked per night)*.`;

  return `Top property types include ${data
    .map(item => `*${generatePropertyTypeSummaryMap(item.propertyType, item.priceTier, currency)}*`)
    .join(' and ')}.`;
};

const CustomTooltip = (props: any) => {
  const { payload, index, isUserAllowed } = props;
  if (!isUserAllowed) {
    return (
      <Box sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="body1" fontWeight={500} sx={{ color: COLOR_TEXT_1 }}>
          Subscribe to unlock the data.
        </Typography>
      </Box>
    );
  }

  if (payload && payload.length) {
    const payloadData = payload[index]?.payload;

    if (!payloadData) return null;

    const { sizeLabel, budget, midscale, luxury } = payloadData;

    const getSectionText = (label: string, value: number, color: string) => {
      return (
        <Typography
          fontWeight={600}
          variant="body1"
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: color, mb: '4px' }}
        >
          <Box
            sx={{
              width: '20px',
              height: '20px',
              background: color,
              borderRadius: '4px',
            }}
          />
          {label}: <span style={{ color: COLOR_TEXT_1 }}>{value}%</span>
        </Typography>
      );
    };

    return (
      <Box sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: COLOR_TEXT_1 }}>
          {sizeLabel}
        </Typography>
        <hr style={{ border: `1px solid ${COLOR_GREY_200}` }} />
        {getSectionText('Occupancy Rate (Budget)', budget, COLORS.budget)}
        {getSectionText('Occupancy Rate (Mid-scale)', midscale, COLORS.midscale)}
        {getSectionText('Occupancy Rate (Luxury)', luxury, COLORS.luxury)}
      </Box>
    );
  }
  return null;
};

const BarCrownLabel = (props: any) => {
  const { top3OccupancyRates, value, isUserAllowed } = props;

  if (value && top3OccupancyRates) {
    const rank = top3OccupancyRates.indexOf(value) + 1;

    if (rank < 1) return null;

    return (
      <text
        x={props.x + props.width / 2}
        y={props.y - 10}
        textAnchor="middle"
        fontSize={12}
        fontWeight={500}
        fill={COLOR_TEXT_2}
        style={isUserAllowed ? {} : { filter: 'blur(4px)' }}
      >
        #{rank}
      </text>
    );
  }

  return null;
};

interface BestPropertyTypeOverviewProps {
  isLoading: boolean;
  marketDisplayName: string;
  isMobileView: boolean;
  handleOnSampleClick: (plan: string) => void;
  data: any;
  marketMedianOccupancyRate: number;
  currency: string;
  isUserAllowed: boolean;
  amenitiesImpactOnPropertyTypes: any;
  isUSMarket?: boolean;
  onOpenRealtorModal?: () => void;
  children?: React.ReactNode;
}

const BestPropertyTypeOverview = ({
  isLoading,
  data,
  marketMedianOccupancyRate,
  marketDisplayName,
  isMobileView,
  handleOnSampleClick,
  currency,
  isUserAllowed,
  amenitiesImpactOnPropertyTypes,
  isUSMarket,
  onOpenRealtorModal,
  children,
}: BestPropertyTypeOverviewProps) => {
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<any[]>([]);
  const [groupedGraphData, setGroupedGraphData] = useState<any[]>([]);
  const [yAxisRange, setYAxisRange] = useState<[number, number]>([0, 100]);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | null>(null);

  const top3OccupancyRates = useRef<number[] | null>(null);

  let top3AmenitiesImpactOnPropertyTypes: string[] | null = amenitiesImpactOnPropertyTypes
    ? Object.keys(amenitiesImpactOnPropertyTypes)
        .map(amenity => [amenity, amenitiesImpactOnPropertyTypes[amenity]])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(amenity => amenitiesObject[amenity[0]])
    : null;

  useEffect(() => {
    if (!data) return;
    const formattedData = isUserAllowed
      ? Object.entries(data).map(([propertyType, value]: [string, any]) => ({
          propertyType: convertSnakeCaseToTitleCase(propertyType),
          occupancyRate: value['occupancy'],
          priceTier: value['adr'],
        }))
      : BEST_PROPERTY_TYPE_FORMATTED_GRAPH_DATA;

    const sortedData = formattedData.sort((a, b) => b.occupancyRate - a.occupancyRate);

    top3OccupancyRates.current = sortedData.slice(0, 3).map(item => item.occupancyRate);

    type TierKey = 'budget' | 'midscale' | 'luxury';

    const sizeLabelMap = {
      small: 'Small (Studio & 1bd)',
      medium: 'Medium (2bds / 3bds)',
      large: 'Large (4+ bds)',
    };

    const groups: Record<
      'small' | 'medium' | 'large',
      { sizeLabel: string; budget?: number; midscale?: number; luxury?: number }
    > = {
      small: { sizeLabel: sizeLabelMap.small },
      medium: { sizeLabel: sizeLabelMap.medium },
      large: { sizeLabel: sizeLabelMap.large },
    };

    formattedData.forEach(item => {
      const lower = item.propertyType.toLowerCase();

      const sizeKey: 'small' | 'medium' | 'large' = lower.startsWith('small')
        ? 'small'
        : lower.startsWith('medium')
        ? 'medium'
        : 'large';

      let tierKey: TierKey;
      if (lower.includes('budget')) tierKey = 'budget';
      else if (lower.includes('midscale')) tierKey = 'midscale';
      else tierKey = 'luxury';

      groups[sizeKey][tierKey] = item.occupancyRate;
    });

    const finalGrouped = ['small', 'medium', 'large'].map(
      key => groups[key as 'small' | 'medium' | 'large']
    );

    setGroupedGraphData(finalGrouped);

    setSummaryText(generateSummaryText(sortedData.slice(0, 2), currency, isUserAllowed));
  }, [data, currency, isUserAllowed]);

  useEffect(() => {
    // Y-axis range from groupedGraphData
    if (groupedGraphData && groupedGraphData.length > 0) {
      const allValues: number[] = [];

      groupedGraphData.forEach(row => {
        ['budget', 'midscale', 'luxury'].forEach(key => {
          const val = (row as any)[key];
          if (typeof val === 'number') allValues.push(val);
        });
      });

      if (allValues.length > 0) {
        const range = calculateDynamicYAxisRange(
          allValues.map(v => ({ occupancyRate: v })),
          5
        );

        setYAxisRange(range);
      }
    }

    // Graph data + summary text
    if (data) {
      const formattedData = Object.entries(data).map(([propertyType, value]: [string, any]) => ({
        propertyType: convertSnakeCaseToTitleCase(propertyType),
        occupancyRate: value['occupancy'],
        priceTier: value['adr'],
      }));

      const sortedData = formattedData.sort((a: any, b: any) => b.occupancyRate - a.occupancyRate);

      const clippedData = sortedData.slice(0, 3);

      setGraphData([
        ...clippedData,
        {
          propertyType: `${marketDisplayName} median`,
          occupancyRate: marketMedianOccupancyRate,
          isReference: true,
        },
      ]);

      const summaryText = generateSummaryText(sortedData.slice(0, 2), currency, isUserAllowed);
      setSummaryText(summaryText);
    }
  }, [
    groupedGraphData,
    data,
    currency,
    isUserAllowed,
    marketDisplayName,
    marketMedianOccupancyRate,
  ]);

  const getAmenitiesImpactOnPropertyTypesText = () => {
    if (!isUserAllowed)
      return `Listings with *locked*, *locked* and *locked* achieve the highest revenue uplift.`;

    if (!top3AmenitiesImpactOnPropertyTypes || top3AmenitiesImpactOnPropertyTypes.length === 0)
      return '';

    if (top3AmenitiesImpactOnPropertyTypes.includes('Pool')) {
      top3AmenitiesImpactOnPropertyTypes = top3AmenitiesImpactOnPropertyTypes.filter(
        amenity => amenity !== 'Pool'
      ) as string[];
      top3AmenitiesImpactOnPropertyTypes.unshift('Pool');
    }

    const amenitiesText =
      top3AmenitiesImpactOnPropertyTypes.length < 2
        ? `*${top3AmenitiesImpactOnPropertyTypes[0]}*`
        : top3AmenitiesImpactOnPropertyTypes
            .map(
              (amenity, index) =>
                `*${amenity}*${
                  index === (top3AmenitiesImpactOnPropertyTypes as string[]).length - 2
                    ? ' and '
                    : index < (top3AmenitiesImpactOnPropertyTypes as string[]).length - 1
                    ? ', '
                    : ''
                }`
            )
            .join('');

    return `Listings with ${amenitiesText} achieve the highest revenue uplift.`;
  };

  if (!isLoading && !data) return null;

  return (
    <AIOverviewCard isLoading={isLoading} isMobileView={isMobileView}>
      {summaryText && <Body handleOnSampleClick={handleOnSampleClick}>{summaryText}</Body>}

      <Body handleOnSampleClick={handleOnSampleClick} sx={{ mt: 1, mb: -1 }}>
        {getAmenitiesImpactOnPropertyTypesText()}
      </Body>

      <SubHeading>
        Top {marketDisplayName} property types vs {marketDisplayName} median performance
      </SubHeading>

      <ReferenceBarChart
        data={graphData.slice(0, 4)}
        xAxisDataKey="propertyType"
        yAxisLabel="Occupancy Rate"
        yAxisDataKey="occupancyRate"
        referenceText={`${marketDisplayName} Median`}
        referenceValue={marketMedianOccupancyRate}
        unit="%"
        isUserAllowed={isUserAllowed}
        isMobileView={isMobileView}
        yAxisRange={yAxisRange}
      />

      <Box sx={{ position: 'relative' }}>
        <ClickAwayListener onClickAway={() => setActiveTooltipIndex(null)}>
          <ResponsiveContainer
            width="100%"
            height={300}
            onMouseLeave={() => setActiveTooltipIndex(null)}
          >
            <BarChart
              data={groupedGraphData}
              barCategoryGap="25%"
              barGap={4}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="sizeLabel"
                axisLine={false}
                tickLine={false}
                style={{
                  fontSize: '0.75rem',
                  fontFamily: 'Roboto',
                  fill: COLOR_TEXT_2,
                  fontWeight: 500,
                }}
              />
              <YAxis
                domain={yAxisRange}
                // tickFormatter={v => `${v}%`}
                tickLine={false}
                axisLine={{
                  stroke: COLOR_TEXT_2,
                  strokeDasharray: '4 4',
                }}
                style={{
                  fontSize: '0.75rem',
                  fontFamily: 'Roboto',
                  fill: COLOR_TEXT_2,
                  fontWeight: 500,
                }}
                label={{
                  value: 'Occupancy Rate',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    textAnchor: 'middle',
                    fontSize: 12,
                    fontWeight: 600,
                    fill: COLOR_TEXT_2,
                  },
                }}
                tick={props => {
                  const { x, y, payload } = props;

                  return (
                    <text
                      x={x}
                      y={y}
                      dy={4}
                      fontSize={12}
                      fontWeight={500}
                      fill={COLOR_TEXT_2}
                      textAnchor="end"
                    >
                      <tspan style={{ filter: !isUserAllowed ? 'blur(5px)' : 'none' }}>
                        {payload.value}
                      </tspan>
                      <tspan>%</tspan>
                    </text>
                  );
                }} 
              />
              <Tooltip
                content={<CustomTooltip index={activeTooltipIndex} isUserAllowed={isUserAllowed} />}
              />
              <Legend
                payload={[
                  { value: 'Budget ($)', type: 'square', color: COLORS.budget },
                  { value: 'Mid-scale ($$)', type: 'square', color: COLORS.midscale },
                  { value: 'Luxury ($$$)', type: 'square', color: COLORS.luxury },
                ]}
                verticalAlign="bottom"
                wrapperStyle={{
                  paddingTop: '16px',
                  fontSize: '14px',
                }}
              />

              <Bar
                dataKey="budget"
                fill={COLORS.budget}
                radius={[6, 6, 0, 0]}
                onMouseEnter={() => setActiveTooltipIndex(0)}
                onClick={() => setActiveTooltipIndex(0)}
              >
                <LabelList
                  dataKey="budget"
                  content={props => (
                    <BarCrownLabel
                      {...props}
                      top3OccupancyRates={top3OccupancyRates.current}
                      isUserAllowed={isUserAllowed}
                    />
                  )}
                />
              </Bar>
              <Bar
                dataKey="midscale"
                fill={COLORS.midscale}
                radius={[6, 6, 0, 0]}
                onMouseEnter={() => setActiveTooltipIndex(1)}
                onClick={() => setActiveTooltipIndex(1)}
              >
                <LabelList
                  dataKey="midscale"
                  content={props => (
                    <BarCrownLabel
                      {...props}
                      top3OccupancyRates={top3OccupancyRates.current}
                      isUserAllowed={isUserAllowed}
                    />
                  )}
                />
              </Bar>
              <Bar
                dataKey="luxury"
                fill={COLORS.luxury}
                radius={[6, 6, 0, 0]}
                onMouseEnter={() => setActiveTooltipIndex(2)}
                onClick={() => setActiveTooltipIndex(2)}
              >
                <LabelList
                  dataKey="luxury"
                  content={props => (
                    <BarCrownLabel
                      {...props}
                      top3OccupancyRates={top3OccupancyRates.current}
                      isUserAllowed={isUserAllowed}
                    />
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ClickAwayListener>

        {!isUserAllowed && (
          <PremiumOverlay
            handleClick={() => handleOnSampleClick('expert')}
            isMobileView={isMobileView}
          />
        )}
      </Box>

      {isUSMarket && onOpenRealtorModal && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <StrPropertiesCTAButton
            label="View STR properties matching top types"
            onClick={onOpenRealtorModal}
          />
        </Box>
      )}

      {children}
    </AIOverviewCard>
  );
};

const PremiumOverlay = ({
  handleClick,
  isMobileView,
}: {
  handleClick: any;
  isMobileView: boolean;
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 75,
        width: 'calc(100% - 75px)',
        height: isMobileView ? 'calc(100% - 100px)' : 'calc(100% - 80px)',
        background: '#fffa',
        backgroundBlendMode: 'multiply',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body1"
        onClick={() => handleClick('expert')}
        sx={{
          fontSize: '20px',
          fontWeight: 500,
          color: COLOR_PRIMARY_2,
          cursor: 'pointer',
          pointerEvents: 'auto',
          textDecoration: 'underline',
        }}
      >
        {useUpgradeMesageProFeature()}
      </Typography>
    </Box>
  );
};

export default BestPropertyTypeOverview;
